import type { ProgramWork } from "../types";

const LETTERS = /\p{L}/gu;
const LATIN_LETTERS = /\p{Script=Latin}/gu;

/** True when a billed title is predominantly written in a non-Latin script
 *  (CJK, Hangul, Cyrillic, Arabic, …). */
function isNonLatin(text: string): boolean {
  const letters = text.match(LETTERS)?.length ?? 0;
  const latin = text.match(LATIN_LETTERS)?.length ?? 0;
  return letters - latin > latin;
}

/** Same qualifier convention as api/pieces.py piece_label(): a catalogue name carries no
 *  part marker, so "Candide" + part=overture must render "Candide (Overture)" or it reads
 *  as the whole operetta. */
const PART_LABELS: Record<string, string> = {
  overture: "Overture",
  suite: "Suite",
};

/** Words that mean the billing knows something the catalogue match doesn't — an excerpt
 *  ("Danse Espagnole *from* La vida breve"), an arrangement, a premiere note. The match
 *  points at the parent work, so replacing such a billing with the bare catalogue title
 *  would silently widen (or annotation-strip) the claim. A false positive here is safe:
 *  it just keeps today's as-billed behavior for that entry. */
const ANNOTATED_BILLING = new RegExp(
  "\\b(" +
    [
      "from", "aus", "extraits?", "tir[ée]e?", // "X from Y" / "aus" / "extrait de"
      "arr", "arranged", "arrangements?", "bearb\\w*", // arrangements
      "transcri\\w*", "orchestrat\\w*", "orch", "version", "fassung",
      "excerpts?", "highlights?", "selections?", "auszü?ge?",
      "movements?", "mvts?", "s[aä]tze?", "acts?", "szene", "scene",
      "premi[eè]re", "uraufführung",
    ].join("|") +
    ")\\b",
  "i",
);

/** What to print for a program entry.
 *
 *  When the works catalogue resolved the entry, display prefers `work_canonical`: billed
 *  titles for one work arrive in every language and house style ("Sinfonía nº 5",
 *  "Ouvertüre zu Figaros Hochzeit"), and the catalogue name is the one spelling that reads
 *  the same in a search-result list as on a card. Composers already work this way — the
 *  same identity resolution, applied to the title.
 *
 *  Guards, in order — each one falls back to the billed string (`work_display`), which is
 *  always safe because it is exactly what the org printed:
 *  - unresolved entries have nothing to swap in;
 *  - a non-Latin billing always swaps when resolved (unreadable to this site's audience);
 *  - part_kind overture/suite renders "Canonical (Overture)" — the part is explicitly
 *    modeled, so no information is lost;
 *  - part_kind excerpt keeps the billing: it usually names WHICH excerpt ("Danse
 *    Espagnole from La vida breve") and the catalogue title can't;
 *  - a billing with excerpt/arrangement/premiere markers keeps the billing even when
 *    part_kind says complete — part detection misses many of these, and the annotation
 *    is real information.
 *  Concert titles are never touched. */
export function workText(entry: ProgramWork): string {
  const billed = entry.work_display || entry.work;
  const canonical = entry.work_canonical;
  if (!canonical || !billed) return billed;

  const part = entry.part_kind && entry.part_kind !== "complete" ? entry.part_kind : null;
  const partLabel = part ? PART_LABELS[part] : null;
  const qualified = partLabel ? `${canonical} (${partLabel})` : canonical;

  if (isNonLatin(billed)) return qualified;
  if (part && !partLabel) return billed; // excerpt (or unknown part): billing names which
  if (partLabel) return qualified;
  if (ANNOTATED_BILLING.test(billed)) return billed;
  return qualified;
}
